import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Stagger } from "../components/motion/Stagger";
import { NumberTicker } from "../components/motion/NumberTicker";
import { AIThinkingOrb } from "../components/motion/AIThinkingOrb";
import {
  Instagram,
  Send,
  CheckCircle2,
  AlertCircle,
  Eye,
  Mail,
  Users,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tiny TikTok glyph (lucide doesn't ship one)
const TikTokIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.5 7.5a6 6 0 0 1-3.5-1.1V15a5 5 0 1 1-5-5v3a2 2 0 1 0 2 2V2h3a3 3 0 0 0 3 3v2.5Z" />
  </svg>
);

const PLATFORMS = [
  { key: "instagram", label: "Instagram", icon: Instagram },
  { key: "tiktok",    label: "TikTok",    icon: TikTokIcon },
];

const DEFAULT_TEMPLATES = {
  instagram: {
    subject: "XEA × Instagram auto-post is live 🎉",
    body:
      "Hey {name},\n\n" +
      "Big news — XEA can now auto-post your generated creatives straight to Instagram, no copy/paste, no extra apps.\n\n" +
      "You're getting this email because you joined the waitlist months ago. Today's the day.\n\n" +
      "Open XEA → AI Video Generator → tap Instagram on any creative. That's it.\n\n" +
      "Thanks for being early,\nThe XEA team",
  },
  tiktok: {
    subject: "XEA × TikTok auto-post is live 🎉",
    body:
      "Hey {name},\n\n" +
      "It's here — XEA now auto-posts your generated creatives to TikTok in one tap.\n\n" +
      "You joined the waitlist months ago, so you're first in line.\n\n" +
      "Open XEA → AI Video Generator → tap TikTok on any creative.\n\n" +
      "Thanks for waiting,\nThe XEA team",
  },
};

export default function AdminWaitlistPage() {
  const { token } = useAuth();
  const [summary, setSummary] = useState({});
  const [platform, setPlatform] = useState("instagram");
  const [subject, setSubject] = useState(DEFAULT_TEMPLATES.instagram.subject);
  const [body, setBody] = useState(DEFAULT_TEMPLATES.instagram.body);
  const [onlyUnsent, setOnlyUnsent] = useState(true);
  const [loading, setLoading] = useState(true);
  const [dryRunCount, setDryRunCount] = useState(null);
  const [sending, setSending] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/waitlist/admin/summary`, { headers });
      setSummary(r.data || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load waitlist counts");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  // When platform changes, reset subject/body to that platform's template
  useEffect(() => {
    const t = DEFAULT_TEMPLATES[platform];
    setSubject(t.subject);
    setBody(t.body);
    setDryRunCount(null);
  }, [platform]);

  const handleDryRun = async () => {
    setDryRunCount(null);
    try {
      const r = await axios.post(
        `${API_URL}/api/waitlist/admin/broadcast`,
        { platform, subject, body, only_unsent: onlyUnsent, dry_run: true },
        { headers }
      );
      setDryRunCount(r.data?.would_send ?? 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dry-run failed");
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are both required.");
      return;
    }
    if (!window.confirm(`Send this email to every ${onlyUnsent ? "un-emailed " : ""}user on the ${platform} waitlist?`)) {
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/waitlist/admin/broadcast`,
        { platform, subject, body, only_unsent: onlyUnsent, dry_run: false },
        { headers }
      );
      toast.success(`Queued ${r.data?.queued || 0} emails. Sending in the background.`);
      // Refresh counts after a beat
      setTimeout(refresh, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <AIThinkingOrb label="Loading waitlist" size={80} />
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="admin-waitlist-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-ink-500">Admin</p>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-[-0.04em] mt-2 leading-[1]">
          Waitlist <span className="text-coral-500">broadcast.</span>
        </h1>
        <p className="text-ink-500 font-light mt-3 max-w-xl">
          Every user who tapped "🔔 Notify me" on Instagram/TikTok auto-post lives here. Email them the moment those APIs go live.
        </p>
      </div>

      {/* Per-platform summary */}
      <Stagger className="grid sm:grid-cols-2 gap-5">
        {PLATFORMS.map(({ key, label, icon: Icon }) => {
          const s = summary[key] || {};
          const active = platform === key;
          return (
            <Stagger.Item key={key}>
              <button
                type="button"
                onClick={() => setPlatform(key)}
                data-testid={`waitlist-platform-${key}`}
                className={`w-full text-left p-7 rounded-3xl border transition-all duration-200 ${
                  active
                    ? "bg-ink-950 text-white border-ink-950 shadow-[0_8px_24px_rgba(10,10,10,0.18)]"
                    : "bg-white border-ink-200 hover:border-ink-400 hover:-translate-y-0.5"
                }`}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${
                    active ? "bg-citrus-500 text-ink-950" : "bg-ink-100 text-ink-950"
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  {active && (
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-citrus-500 text-ink-950">
                      Selected
                    </span>
                  )}
                </div>
                <p className="font-display font-bold text-xl tracking-tight">{label}</p>
                <div className="mt-5 grid grid-cols-3 gap-4">
                  <Stat label="Total" value={s.total || 0} active={active} />
                  <Stat label="Pending" value={s.pending || 0} active={active} accent="coral" />
                  <Stat label="Emailed" value={s.already_emailed || 0} active={active} />
                </div>
                {s.last_broadcast_at && (
                  <p className={`text-[11px] mt-4 ${active ? "text-white/60" : "text-ink-500"}`}>
                    Last broadcast: {new Date(s.last_broadcast_at).toLocaleString()}
                  </p>
                )}
              </button>
            </Stagger.Item>
          );
        })}
      </Stagger>

      {/* Compose */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Mail className="w-5 h-5 text-coral-500" />
            Compose broadcast — {platform}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-[0.18em] text-ink-500">Subject</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="A short, punchy subject line"
              maxLength={200}
              data-testid="waitlist-subject"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-[0.18em] text-ink-500">
              Body <span className="text-ink-300 normal-case tracking-normal font-medium">— use {"{name}"} to personalize</span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              maxLength={10000}
              className="w-full rounded-2xl border border-ink-200 bg-ink-100 px-5 py-4 text-sm text-ink-950 transition-all focus:outline-none focus:bg-white focus:border-ink-950 focus:shadow-[0_0_0_4px_rgba(10,10,10,0.06)]"
              data-testid="waitlist-body"
            />
          </div>

          <label className="flex items-center gap-3 text-sm text-ink-700 select-none cursor-pointer">
            <input
              type="checkbox"
              checked={onlyUnsent}
              onChange={(e) => setOnlyUnsent(e.target.checked)}
              className="w-4 h-4 rounded border-ink-300 text-ink-950 focus:ring-ink-950"
              data-testid="waitlist-only-unsent"
            />
            <span>
              <strong className="text-ink-950">Skip users who've already received a broadcast</strong> for this platform (recommended)
            </span>
          </label>

          {dryRunCount !== null && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 p-4 rounded-2xl bg-citrus-500/15 border border-citrus-500/40"
              data-testid="waitlist-dry-run-result"
            >
              <Users className="w-4 h-4 text-ink-950 shrink-0" />
              <p className="text-sm text-ink-950">
                This will send to <strong><NumberTicker value={dryRunCount} duration={0.6} /></strong> {dryRunCount === 1 ? "person" : "people"}.
              </p>
            </motion.div>
          )}

          <div className="flex flex-wrap gap-3 pt-2">
            <Button variant="outline" onClick={handleDryRun} data-testid="waitlist-dry-run-btn">
              <Eye className="w-4 h-4" /> Preview count
            </Button>
            <Button
              variant="default"
              onClick={handleSend}
              disabled={sending}
              data-testid="waitlist-send-btn"
            >
              <Send className={`w-4 h-4 ${sending ? "animate-pulse" : ""}`} />
              {sending ? "Queuing…" : "Send broadcast"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Footer reminder */}
      <div className="rounded-2xl border border-ink-200/70 bg-ink-50 p-5 flex items-start gap-3">
        <AlertCircle className="w-4 h-4 text-coral-500 mt-0.5 shrink-0" />
        <div className="text-sm text-ink-700 leading-relaxed">
          <p className="font-semibold text-ink-950">SMTP throttling note</p>
          <p className="text-ink-500 font-light mt-1">
            We send via your Gmail SMTP. Gmail caps free sends at ~500/day. If your waitlist grows past that, switch SMTP_USER to a paid SendGrid or Postmark account in the backend env.
          </p>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, active, accent }) {
  const tone = accent === "coral"
    ? (active ? "text-citrus-500" : "text-coral-500")
    : (active ? "text-white" : "text-ink-950");
  const labelTone = active ? "text-white/60" : "text-ink-500";
  return (
    <div>
      <p className={`font-display font-black text-2xl tracking-[-0.04em] leading-none ${tone}`}>
        <NumberTicker value={value} duration={0.5} />
      </p>
      <p className={`text-[10px] font-bold uppercase tracking-[0.18em] mt-1 ${labelTone}`}>
        {label}
      </p>
    </div>
  );
}
